import React, { useState } from "react";

interface FilterOption {
  label: string;
  value: string;
}

interface FilterGroup {
  label: string;
  options: FilterOption[];
}

export const CohortFilters: React.FC = () => {
  const [selectedVintage, setSelectedVintage] = useState("2025 Q1");
  const [selectedRiskBand, setSelectedRiskBand] = useState("Prime 680+");
  const [selectedLTV, setSelectedLTV] = useState("< 65%");
  const [selectedRegion, setSelectedRegion] = useState("National");

  const vintages = ["2025 Q1", "2024 Q4", "2024 Q3", "2024 Q2"];
  const riskBands = ["Prime 680+", "Alt-A 620-679", "B-Lend < 620"];
  const ltvBuckets = ["< 65%", "65-80%", "> 80%"];
  const regions = ["National", "ON/BC focus", "AB/MB/SK"];

  return (
    <article className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Cohort filters
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Slice by origination period, credit band and LTV buckets.
          </div>
        </div>
        <span className="rounded-full px-2.25 py-0.75 text-[10px] uppercase tracking-[0.12em] border border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.12)]">
          Interactive
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2.5 text-xs">
        {/* Origination Vintage */}
        <div className="p-2.5 rounded-xl bg-[rgba(5,7,22,0.9)] border border-[rgba(139,146,176,0.5)] flex flex-col gap-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#8b92b0]">
            Origination vintage
          </div>
          <div className="flex flex-wrap gap-1.5">
            {vintages.map((vintage) => (
              <span
                key={vintage}
                onClick={() => setSelectedVintage(vintage)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedVintage === vintage
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
                }`}
              >
                {vintage}
              </span>
            ))}
          </div>
        </div>

        {/* FICO / Risk Band */}
        <div className="p-2.5 rounded-xl bg-[rgba(5,7,22,0.9)] border border-[rgba(139,146,176,0.5)] flex flex-col gap-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#8b92b0]">
            FICO / Risk band
          </div>
          <div className="flex flex-wrap gap-1.5">
            {riskBands.map((band) => (
              <span
                key={band}
                onClick={() => setSelectedRiskBand(band)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedRiskBand === band
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
                }`}
              >
                {band}
              </span>
            ))}
          </div>
        </div>

        {/* LTV Bucket */}
        <div className="p-2.5 rounded-xl bg-[rgba(5,7,22,0.9)] border border-[rgba(139,146,176,0.5)] flex flex-col gap-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#8b92b0]">
            LTV bucket
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ltvBuckets.map((ltv) => (
              <span
                key={ltv}
                onClick={() => setSelectedLTV(ltv)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedLTV === ltv
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
                }`}
              >
                {ltv}
              </span>
            ))}
          </div>
        </div>

        {/* Region */}
        <div className="p-2.5 rounded-xl bg-[rgba(5,7,22,0.9)] border border-[rgba(139,146,176,0.5)] flex flex-col gap-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#8b92b0]">
            Region
          </div>
          <div className="flex flex-wrap gap-1.5">
            {regions.map((region) => (
              <span
                key={region}
                onClick={() => setSelectedRegion(region)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedRegion === region
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
                }`}
              >
                {region}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-3 gap-2 mt-2">
        <div className="p-2 rounded-xl bg-[rgba(5,7,22,0.92)] border border-[rgba(139,146,176,0.5)] text-[11px] flex flex-col gap-1">
          <div className="text-[#8b92b0] uppercase tracking-[0.12em]">
            Cohort size
          </div>
          <div className="font-jetbrains-mono text-[13px]">2 384 loans</div>
          <div className="text-[11px] text-[#8b92b0]">Avg balance $472k</div>
        </div>
        <div className="p-2 rounded-xl bg-[rgba(5,7,22,0.92)] border border-[rgba(139,146,176,0.5)] text-[11px] flex flex-col gap-1">
          <div className="text-[#8b92b0] uppercase tracking-[0.12em]">
            Weighted rate
          </div>
          <div className="font-jetbrains-mono text-[13px]">7.92%</div>
          <div className="text-[11px] text-[#8b92b0]">
            Spread vs prime: +218 bps
          </div>
        </div>
        <div className="p-2 rounded-xl bg-[rgba(5,7,22,0.92)] border border-[rgba(139,146,176,0.5)] text-[11px] flex flex-col gap-1">
          <div className="text-[#8b92b0] uppercase tracking-[0.12em]">
            Avg LTV at origination
          </div>
          <div className="font-jetbrains-mono text-[13px]">73%</div>
          <div className="text-[11px] text-[#8b92b0]">
            Updated using latest AVMs
          </div>
        </div>
      </div>
    </article>
  );
};
